// AICA Missed Calls Analytics Page
// The counters and the table by employee come from the server
// (GET /analytics/missed-calls — the flow's missed-call definitions, calls only,
// no cap of 200 rows); «Recent» is the records list for the same window.
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { buildTimestampsTooltip, formatDateLocale } from '@/lib/utils';
import { DashboardBody } from '@/components/layout/DashboardBody';
import { PeriodBar, type Period } from '@/components/layout/PeriodBar';
import { useExactReportWindow } from '@/hooks/useExactReportWindow';
import { ProjectMultiSelect } from '@/components/filters';
import {
  parseFlowReportSearch,
  type FlowReportSearch,
} from '@/components/analytics/flow/flow-report-search';
import { KpiCardV2 } from '@/components/dashboard';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { FeatureGate } from '@/components/ui/FeatureGate';
import { PhoneMissed, PhoneIncoming, PhoneOutgoing, Loader2 } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { useApiClient } from '@/hooks/useApiClient';
import { useRole } from '@/contexts/RoleContext';
import { useDemoFilterFromAuth } from '@/features/demo/useDemoFilter';
import { useMissedCalls } from '@/hooks/useCallStats';
import { recordsListOptions } from '@/lib/query-options';
import { toRecordItems, type RecordItem } from '@/lib/api/mappers';
import { formatMissedShare, missedSharePercent } from '@/lib/missed-share';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(
  isoString: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('missedCalls.relativeTime.justNow');
  if (diffMins < 60) return t('missedCalls.relativeTime.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('missedCalls.relativeTime.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('missedCalls.relativeTime.daysAgo', { count: diffDays });

  return formatDateLocale(date);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MissedCallRow({
  record,
  t,
}: {
  record: RecordItem;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  // The flow's directions: only `inbound` / `outbound` are known, anything
  // else is a missed call of unknown direction (outside the share).
  const direction =
    record.callDirection === 'inbound' || record.callDirection === 'outbound'
      ? record.callDirection
      : 'unknown';
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0">
      {direction === 'inbound' ? (
        <PhoneIncoming className="h-3.5 w-3.5 shrink-0 text-danger" />
      ) : direction === 'outbound' ? (
        <PhoneOutgoing className="h-3.5 w-3.5 shrink-0 text-warning" />
      ) : (
        <PhoneMissed className="h-3.5 w-3.5 shrink-0 text-fg-muted" />
      )}
      <span className="text-sm text-fg truncate min-w-0">
        {record.participantName || record.crmEntityName || record.title}
      </span>
      {record.agentName && (
        <span className="text-xs text-fg-muted shrink-0">{record.agentName}</span>
      )}
      <span
        className={`text-micro font-medium px-1.5 py-0.5 rounded shrink-0 ${
          direction === 'inbound'
            ? 'bg-danger-dim text-danger-fg'
            : direction === 'outbound'
              ? 'bg-warning-dim text-warning-fg'
              : 'bg-bg-elevated text-fg-muted'
        }`}
      >
        {t(
          direction === 'inbound'
            ? 'missedCalls.direction.employeeNoAnswer'
            : direction === 'outbound'
              ? 'missedCalls.direction.clientNoAnswer'
              : 'missedCalls.direction.unknown',
          { ns: 'analyses' }
        )}
      </span>
      {/* Время звонка, а не приёма. Подсказка называет обе отметки: они
          расходятся, когда вебхук портала до нас не дошёл и запись подобрал
          backfill-крон, и без подписи это расхождение читается как наша
          задержка (#359). */}
      <span
        className="text-xs text-fg-faint ml-auto shrink-0"
        title={buildTimestampsTooltip(record.eventTime, record.createdAt, {
          call: t('records:records.timestamps.call', { defaultValue: 'Call' }),
          received: t('records:records.timestamps.received', {
            defaultValue: 'Received',
          }),
        })}
      >
        {record.eventTime
          ? formatRelativeTime(record.eventTime, t)
          : formatRelativeTime(record.createdAt, t)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const NO_LINK_SCOPE: FlowReportSearch = {};
const NO_CHANGE = () => {};

// Exported (not just used via `Route`) so it can be unit-tested directly.
export function MissedCallsPage({
  search = NO_LINK_SCOPE,
  onSearchChange = NO_CHANGE,
}: {
  search?: FlowReportSearch;
  /** Merge a change into the address (replacing the history entry). */
  onSearchChange?: (patch: Partial<FlowReportSearch>) => void;
}) {
  const { t } = useTranslation(['analyses', 'records', 'common']);
  useDocumentTitle(t('missedCalls.metaTitle', { ns: 'analyses' }));

  const [period, setPeriod] = useState<Period>('today');
  // The window of the dashboard card that linked here wins until the first step.
  const { dateRange, navigation, onPeriodChange } = useExactReportWindow(
    search,
    onSearchChange,
    period,
    setPeriod
  );
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  // The same policy as the numbers' query (`useMissedCalls`): not asked before
  // the demo status is known — until then `includeDemo` reads 'no'. A rule the
  // link named (the card's own filter) needs no status.
  const policy = useDemoFilterFromAuth(apiClient, selectedOrgId);
  const includeDemo = search.demo ?? policy.includeDemo;
  const demoLoading = search.demo === undefined && policy.isLoading;

  // The numbers: counted on the server, over the records the viewer may open.
  const {
    data: missed,
    isLoading: missedLoading,
    isFetching: missedFetching,
    error: missedError,
    refetch: refetchMissed,
  } = useMissedCalls({
    period,
    periodStart: dateRange.periodStart,
    periodEnd: dateRange.periodEnd,
    projectIds: search.projectIds,
    includeDemo: search.demo,
  });

  // «Recent»: the records list for the same window, demo toggle and kind of
  // record (a missed CALL) as the numbers above it.
  const {
    data: recentData,
    isLoading: recentLoading,
    isError: recentFailed,
    refetch: refetchRecent,
  } = useQuery({
    ...recordsListOptions(apiClient, selectedOrgId, {
      callCategory: 'missed',
      type: 'call',
      limit: 20,
      projectIds: search.projectIds,
      includeDemo,
      createdAfter: dateRange.periodStart,
      // `periodEnd` is the period's last millisecond, and both bounds keep it:
      // the counters' route turns it into `< periodEnd + 1 ms`, the list reads
      // `createdBefore` inclusively — one set of records, the call at `.999`
      // in both (#1911).
      createdBefore: dateRange.periodEnd,
    }),
    enabled: !!selectedOrgId && !demoLoading,
    select: (raw) => ({ records: toRecordItems(raw.records, t) }),
  });

  const recentRecords = useMemo(() => recentData?.records ?? [], [recentData?.records]);
  const missedShare = missed?.share ?? null;
  const employees = missed?.byEmployee ?? [];
  // The «Not assigned» row is not an employee; a table cut at the ceiling
  // counts only its own rows, which the truncation note already explains.
  const employeesCount = employees.filter((e) => e.agentId !== null).length;
  // The server says when access closes the table: then it is «hidden», not «none».
  const employeesHidden = missed?.byEmployeeHidden === true;

  const handleRefresh = useCallback(() => {
    void refetchMissed();
    void refetchRecent();
  }, [refetchMissed, refetchRecent]);

  return (
    <DashboardBody
      title={t('missedCalls.title', { ns: 'analyses' })}
      subtitle={t('missedCalls.subtitle', { ns: 'analyses' })}
      icon={PhoneMissed}
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
          <BreadcrumbItem current>
            {t('missedCalls.breadcrumb', { ns: 'analyses' })}
          </BreadcrumbItem>
        </Breadcrumb>
      }
      onRefresh={handleRefresh}
      isRefreshing={missedFetching && !missedLoading}
      showExport
    >
      {/* Period selector */}
      <PeriodBar
        periods={[
          { id: 'today', label: t('missedCalls.period.today', { ns: 'analyses' }) },
          { id: 'week', label: t('missedCalls.period.week', { ns: 'analyses' }) },
          { id: 'month', label: t('missedCalls.period.month', { ns: 'analyses' }) },
        ]}
        activePeriod={period}
        onPeriodChange={onPeriodChange}
        navigation={navigation}
        isLoading={missedFetching && !missedLoading}
        actions={
          <ProjectMultiSelect
            className="w-44"
            selectedProjectIds={search.projectIds ?? []}
            onChange={(ids) =>
              onSearchChange({ projectIds: ids.length > 0 ? ids : undefined })
            }
          />
        }
      />

      {/* The numbers: no data yet and no error is pending (or the query waits
          for the organization) — a spinner; a failed refetch keeps the numbers
          shown. Only the numbers and their table wait on this request:
          «Recent» below is its own request and shows whatever this one does. */}
      {!missed && !missedError ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-fg-muted" />
        </div>
      ) : !missed ? (
        <div className="flex items-center justify-center py-16 text-sm text-danger">
          {t('missedCalls.error.loadFailed', { ns: 'analyses' })}
        </div>
      ) : (
        /* KPI cards: the flow's definitions — calls only, the unknown
           direction apart and outside the share. */
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <KpiCardV2
            label={t('missedCalls.kpi.missedInbound', { ns: 'analyses' })}
            value={missed.inbound}
            color="danger"
            subtitle={t('missedCalls.kpi.missedInboundHint', { ns: 'analyses' })}
          />
          <KpiCardV2
            label={t('missedCalls.kpi.clientNoAnswer', { ns: 'analyses' })}
            value={missed.outbound}
            color="warning"
            subtitle={t('missedCalls.kpi.clientNoAnswerHint', { ns: 'analyses' })}
          />
          <KpiCardV2
            label={t('missedCalls.kpi.unknownDirection', { ns: 'analyses' })}
            value={missed.unknownDirection}
            color="primary"
            subtitle={t('missedCalls.kpi.unknownDirectionHint', { ns: 'analyses' })}
          />
          <KpiCardV2
            label={t('missedCalls.kpi.missRate', { ns: 'analyses' })}
            value={formatMissedShare(t, missedSharePercent(missedShare))}
            color="warning"
            subtitle={t('missedCalls.kpi.missRateHint', { ns: 'analyses' })}
          />
        </div>
      )}

      {/* Two-column detail panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Missed by employee table — it comes with the numbers */}
        {missed && (
          <DashboardPanel
            title={t('missedCalls.panels.missedByAgent', { ns: 'analyses' })}
            action={
              // No count over a table of the «Not assigned» row alone.
              employeesHidden || missed.truncated || employeesCount === 0 ? undefined : (
                <span className="text-xs text-fg-muted">
                  {t('missedCalls.panels.agentsCount', {
                    ns: 'analyses',
                    count: employeesCount,
                  })}
                </span>
              )
            }
          >
            <div className="-m-4">
              {employeesHidden ? (
                <div className="text-center py-8 text-fg-muted text-sm">
                  {t('missedCalls.table.hidden', { ns: 'analyses' })}
                </div>
              ) : employees.length === 0 ? (
                <div className="text-center py-8 text-fg-muted text-sm">
                  {t('missedCalls.noMissedCallsFound', { ns: 'analyses' })}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-4 py-3 bg-bg-surface text-sm font-medium text-fg-muted">
                        {t('missedCalls.table.agent', { ns: 'analyses' })}
                      </th>
                      <th className="px-4 py-3 bg-bg-surface text-sm font-medium text-danger text-right">
                        {t('missedCalls.table.agentMissed', { ns: 'analyses' })}
                      </th>
                      <th className="px-4 py-3 bg-bg-surface text-sm font-medium text-warning text-right">
                        {t('missedCalls.table.clientNoAnswer', { ns: 'analyses' })}
                      </th>
                      <th className="px-4 py-3 bg-bg-surface text-sm font-medium text-fg-muted text-right">
                        {t('missedCalls.table.unknownDirection', { ns: 'analyses' })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((employee) => (
                      <tr
                        key={JSON.stringify([
                          employee.installationId,
                          employee.key ?? employee.agentId,
                        ])}
                        className="border-b border-border last:border-0 hover:bg-bg-elevated transition-colors"
                      >
                        <td className="px-4 py-3 text-fg">
                          {employee.agentId === null
                            ? t('missedCalls.table.unassigned', { ns: 'analyses' })
                            : (employee.agentName ?? employee.agentId)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-danger">
                          {employee.inbound > 0 ? employee.inbound : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-warning">
                          {employee.outbound > 0 ? employee.outbound : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-fg-muted">
                          {employee.unknownDirection > 0
                            ? employee.unknownDirection
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {missed.truncated && (
              <p
                className="mt-6 text-xs text-fg-muted"
                data-testid="missed-calls-truncated"
              >
                {t('missedCalls.table.truncated', {
                  ns: 'analyses',
                  count: employeesCount,
                })}
              </p>
            )}
          </DashboardPanel>
        )}

        {/* Right: Recent Missed Calls */}
        <DashboardPanel
          title={t('missedCalls.panels.recentMissedCalls', { ns: 'analyses' })}
          action={
            recentData ? (
              <span className="text-xs text-fg-muted">
                {t('missedCalls.panels.shownCount', {
                  ns: 'analyses',
                  count: recentRecords.length,
                })}
              </span>
            ) : undefined
          }
        >
          <div className="-m-4">
            {/* The list is its own request: pending or failed is not «none». */}
            {recentLoading || demoLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
              </div>
            ) : recentFailed && !recentData ? (
              <div className="text-center py-8 text-danger text-sm">
                {t('missedCalls.error.loadFailed', { ns: 'analyses' })}
              </div>
            ) : recentRecords.length === 0 ? (
              <div className="text-center py-8 text-fg-muted text-sm">
                {t('missedCalls.noMissedCallsFound', { ns: 'analyses' })}
              </div>
            ) : (
              recentRecords.map((record) => (
                <MissedCallRow key={record.id} record={record} t={t} />
              ))
            )}
          </div>
        </DashboardPanel>
      </div>
    </DashboardBody>
  );
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/analytics/missed-calls')({
  validateSearch: (search: Record<string, unknown>): FlowReportSearch =>
    parseFlowReportSearch(search),
  component: function MissedCallsPageGated() {
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const onSearchChange = useCallback(
      (patch: Partial<FlowReportSearch>) =>
        void navigate({
          search: (previous) => ({ ...previous, ...patch }),
          replace: true,
        }),
      [navigate]
    );
    return (
      <FeatureGate flag="enableAnalytics">
        <MissedCallsPage search={search} onSearchChange={onSearchChange} />
      </FeatureGate>
    );
  },
});
